import {LinkButtonSmall} from '@components/common/button/button'
import {
  DropdownList,
  DropdownMultiSelectList
} from '@components/common/dropdown-list/dropdown-list'
import {Option} from '@components/common/select/select'
import {Remove, Separator} from '@components/statistics/stats-reports/common'
import {
  getLocationFilterRows,
  ProjectsTree
} from '@components/statistics/stats-reports/filter-components/location-filter-rows'
import React, {useEffect, useLayoutEffect, useState} from 'react'
import {Lang} from 'yougile-lang'
import {
  DataObjectId,
  locationFilterArrayToSubtasks,
  LocationFilterItem,
  LocationFilterSubtaskLevel,
  locationFilterSubtasksToArray,
  noBoardContainerId
} from 'yougile-model'

interface OuterProps {
  showInputForEmptyFilters?: boolean
  filters: LocationFilterItem[]
  dontOpen?: boolean
  hideRemoveButton?: boolean
  hideSubtasksOptions?: boolean
  onOpenChange?: (isOpen: boolean) => void
}

interface Props extends OuterProps {
  update: (filters: LocationFilterItem[]) => void
}

interface SelectorsProps {
  projects: Option<DataObjectId>[]
  boards: Option<DataObjectId>[]
  allBoardColumns: Option<DataObjectId>[]
  projectId?: DataObjectId
  boardId?: DataObjectId
  selectedColumns: DataObjectId[]
  updateSelector: (filters: DataObjectId[]) => void
  tree: ProjectsTree
  initialOpen: boolean
  onOpenChange?: (isOpen: boolean) => void
}

const Selectors = (props: SelectorsProps) => {
  const onBoardChange = (boardId: DataObjectId) => {
    if (boardId) {
      const project = props.tree.find(x => x.project.id === props.projectId)
      const board = project.boards.find(x => x.board.id === boardId)
      const newFilter = board.columns.length
        ? board.columns.map(x => x.id)
        : [boardId]
      props.updateSelector(newFilter)
    } else {
      props.updateSelector([props.projectId])
    }
  }
  return (
    <div className="flex">
      <DropdownList<DataObjectId | undefined>
        className="w-160"
        initialOpen={props.initialOpen}
        value={props.projectId}
        onChange={x => props.updateSelector([x])}
        placeholder={Lang.msg.statsTimeline.anyProject}
        options={props.projects}
        onOpenChange={isOpen => {
          props?.onOpenChange(isOpen)
        }}
      />
      {!!props.boards.length &&
        props.projectId &&
        props.projectId !== noBoardContainerId && (
          <>
            <Separator />
            <DropdownList<DataObjectId | undefined>
              className="w-160"
              value={props.boardId}
              onChange={onBoardChange}
              options={props.boards}
              onOpenChange={isOpen => {
                props?.onOpenChange(isOpen)
              }}
            />
          </>
        )}
      {!!props.allBoardColumns.length && (
        <>
          <Separator />
          <DropdownMultiSelectList<DataObjectId>
            className="w-160"
            value={props.selectedColumns}
            onChange={x => props.updateSelector(x.length ? x : [props.boardId])}
            options={props.allBoardColumns}
            allText={Lang.msg.statsFilterAnyColumn}
            onOpenChange={isOpen => {
              props?.onOpenChange(isOpen)
            }}
          />
        </>
      )}
    </div>
  )
}

const getOptions = (
  items: {id: DataObjectId; title: string}[],
  emptyLabel?: string
) => {
  const options = (items ?? []).map(x => ({label: x.title, value: x.id}))
  if (emptyLabel) {
    options.unshift({
      value: undefined,
      label: emptyLabel
    })
  }
  return options
}

const getAllSubtaskLevels = (): Option<LocationFilterSubtaskLevel>[] => [
  {value: 'level0', label: Lang.msg.subtask.filterNotSubtasks},
  {value: 'level1', label: Lang.msg.subtask.filterLevel1},
  {value: 'other', label: Lang.msg.subtask.filterOther}
]

// eslint-disable-next-line max-lines-per-function
export const LocationFilter = (props: Props) => {
  const {
    filters = [],
    update,
    dontOpen = false,
    hideSubtasksOptions = false,
    hideRemoveButton = false,
    onOpenChange,
    showInputForEmptyFilters = false
  } = props
  const subtasks = new Map<DataObjectId, LocationFilterItem['subtasks']>()
  for (const filter of filters) {
    subtasks.set(filter.parent, filter.subtasks)
  }
  const getDefaultSubtasks = () => {
    return hideSubtasksOptions
      ? {level0: true, level1: true, other: true}
      : {level0: true}
  }
  const idToFilter = (id: DataObjectId): LocationFilterItem => {
    return {
      parent: id,
      subtasks: subtasks.get(id) ?? getDefaultSubtasks()
    }
  }
  const {rows, tree} = getLocationFilterRows(filters, showInputForEmptyFilters)
  const [initialOpen, setInitialOpen] = useState(false)
  useEffect(() => {
    if (typeof onOpenChange === 'function') {
      onOpenChange(initialOpen)
    }
  }, [initialOpen])

  const allProjectOptions = getOptions(tree.map(x => x.project))
  const addNewLocation = () => {
    setInitialOpen(true)
    update(
      filters.concat([
        {parent: tree[0].project.id, subtasks: getDefaultSubtasks()}
      ])
    )
  }
  useLayoutEffect(() => {
    if ((!filters || filters.length === 0) && !dontOpen) {
      addNewLocation()
    }
  }, [])

  const updateFilters = (filterIndex: number, newIds: DataObjectId[]) => {
    const allNewIds = rows.flatMap((x, index) =>
      index === filterIndex ? newIds : x.filters
    )
    if (allNewIds.length > 0) {
      update(allNewIds.map(idToFilter))
    } else {
      update(undefined)
    }
  }

  const updateSubtasks = (
    filterIndex: number,
    type: LocationFilterSubtaskLevel[]
  ) => {
    const subtasks = locationFilterArrayToSubtasks(type)
    const row = rows[filterIndex]
    const newFilters: LocationFilterItem[] = []
    for (const filter of filters) {
      const newFilter = row.filters.includes(filter.parent)
        ? {parent: filter.parent, subtasks}
        : filter
      newFilters.push(newFilter)
    }
    update(newFilters)
  }
  const allSelectedBoards = new Set(rows.map(x => x.boardId))

  return (
    <div>
      {rows.length > 0 && (
        <div className="space-y-8">
          {rows.map((row, i) => {
            row.filters
            return (
              <React.Fragment key={i}>
                <div className="flex justify-between">
                  <Selectors
                    projects={allProjectOptions}
                    initialOpen={initialOpen && i === rows.length - 1}
                    boards={getOptions(
                      row.boards
                        .map(x => x.board)
                        .filter(
                          x =>
                            x.id === row.boardId || !allSelectedBoards.has(x.id)
                        ),
                      Lang.msg.statsFilterAnyBoard
                    )}
                    allBoardColumns={getOptions(row.allBoardColumns)}
                    projectId={row.projectId}
                    boardId={row.boardId}
                    selectedColumns={row.selectedColumns}
                    updateSelector={newIds => updateFilters(i, newIds)}
                    tree={tree}
                    onOpenChange={isOpen => {
                      if (typeof props?.onOpenChange === 'function') {
                        onOpenChange(isOpen)
                      }
                    }}
                  />
                  {!hideRemoveButton && (
                    <Remove onClick={() => updateFilters(i, [])} />
                  )}
                </div>
                {!hideSubtasksOptions && (
                  <div>
                    <DropdownMultiSelectList<LocationFilterSubtaskLevel>
                      link
                      className="ml-10"
                      options={getAllSubtaskLevels()}
                      value={locationFilterSubtasksToArray(
                        subtasks.get(row.filters[0])
                      )}
                      onChange={value => updateSubtasks(i, value)}
                    />
                  </div>
                )}
              </React.Fragment>
            )
          })}
        </div>
      )}
      {(filters.length || !showInputForEmptyFilters) && (
        <LinkButtonSmall
          className={!dontOpen && 'ml-4 mt-4'}
          onClick={addNewLocation}
        >
          {Lang.msg.statsAddLocation}
        </LinkButtonSmall>
      )}
    </div>
  )
}

export const LocationFilterValue = (props: OuterProps) => {
  const filters = props.filters ?? []
  const {rows, tree} = getLocationFilterRows(filters)
  const allSelectedBoards = new Set(rows.map(x => x.boardId))
  const allProjectOptions = getOptions(tree.map(x => x.project))
  const boards = rows.map(row => {
    const opts = getOptions(
      row.boards
        .map(x => x.board)
        .filter(x => x.id === row.boardId || allSelectedBoards.has(x.id))
    )
    return {
      board: opts[0],
      numberOfSelectedColumns: row.selectedColumns.length
    }
  })
  const projectsId = rows.map(row => row.projectId)
  const projects = allProjectOptions.filter(opt =>
    projectsId.includes(opt.value)
  )
  const result = projects.map((project, i) => ({
    project: project.label,
    board: boards[i].board?.label,
    columns: boards[i].numberOfSelectedColumns
  }))

  return (
    <>
      {result.map((el, ix, {length}) => (
        <div className="flex items-center" key={el.project}>
          <span className="truncate max-w-128">{el.project}</span>
          {el.board && <Separator />}
          <span className="truncate max-w-128">{el.board}</span>
          {el.columns > 0 && (
            <>
              <Separator />
              <span className="truncate max-w-128">
                {el.columns}{' '}
                {declOfNum(el.columns, [
                  Lang.msg.statsTimeline.column1,
                  Lang.msg.statsTimeline.column2,
                  Lang.msg.statsTimeline.column3
                ])}
              </span>
            </>
          )}
          {ix + 1 !== length && <span className="mr-4">,</span>}
        </div>
      ))}
    </>
  )
}

const declOfNum = (number: number, titles: string[]) => {
  const cases = [2, 0, 1, 1, 1, 2]
  return titles[
    number % 100 > 4 && number % 100 < 20
      ? 2
      : cases[number % 10 < 5 ? number % 10 : 5]
  ]
}
